import { useMemo } from "react";
import { InvoiceModel, ChargeType } from "../../../data/entities";
import { BookingEventModel } from "../../../data/entities/booking/BookingEventModel";
import { formatPrice, to12HourTime, formatDateLong, formatDateToLongUsWeekday } from "../../../utils";
import { usePaymentModalCalculations } from "../../invoice/hooks";

export interface AppointmentItemProps {
  event: BookingEventModel;
  invoice: InvoiceModel;
  bookingDate: Date | undefined | null;
  isLast: boolean;
  eventIndex: number;
}

export const AppointmentItem = ({ event, invoice, bookingDate, isLast, eventIndex }: AppointmentItemProps) => {
  const eventInvoice = useMemo(() => {
    if (invoice.items.length === 1) {
      return invoice;
    }
    const item = invoice.items[eventIndex];
    return {
      ...invoice,
      items: item ? [item] : []
    } as InvoiceModel;
  }, [invoice, eventIndex]);

  const calculations = usePaymentModalCalculations(eventInvoice);
  
  const getChargeLabel = () => {
    if (calculations.totalCharges === 0 && calculations.totalHolds === 0) {
      return "Appointment Booked";
    }
    
    if (calculations.hasCharges) {
      return "Paid Today";
    }
    if (calculations.hasHolds) {
      return "Held Today";
    }
    return "Appointment Booked";
  };

  const displayAmount = calculations.hasCharges 
    ? calculations.totalCharges 
    : calculations.totalHolds;
  
  const displaySubtotal = calculations.hasCharges 
    ? calculations.chargesSubtotal 
    : calculations.holdsSubtotal;
  
  const displayTax = calculations.hasCharges 
    ? calculations.chargesTax 
    : calculations.holdsTax;

  const isZeroAmount = displayAmount === 0;

  const desktopDate = formatDateToLongUsWeekday(bookingDate) ?? 'N/A';
  const mobileDate = bookingDate ? formatDateLong(bookingDate) : 'N/A';


  const displayEndTime = useMemo(() => {
    if (!event.startTime || !event.service?.defaultDuration) return event.endTime;
    const [hours, minutes] = event.startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + event.service.defaultDuration;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
  }, [event.startTime, event.service?.defaultDuration, event.endTime]);

  return (
    <>
      <div className="p-3 p-md-4">
        {/* Service Name */}
        <div className="mb-3">
          <h5 className="mb-1">{event.service?.name}</h5>
          {(event.provider?.name || event.service?.allowAnyProvider) && (
            <p className="text-muted small mb-0">
              with {event.provider?.name ?? 'Any Provider'}
            </p>
          )}
        </div>

        {/* Booking Info */}
        <div className="mb-3">
          <div className="mb-2">
            <span className="text-muted small d-block mb-1">Date</span>
            <span className="d-none d-md-inline fw-semibold">{desktopDate}</span>
            <span className="d-md-none fw-semibold">{mobileDate}</span>
          </div>

          <div className="mb-2">
            <span className="text-muted small d-block mb-1">Time</span>
            {/* Desktop: time range */}
            <span className="d-none d-md-inline fw-semibold">
              {to12HourTime(event.startTime)}
              {/* - {to12HourTime(displayEndTime)} */}
            </span>
            {/* Mobile: start time only */}
            <span className="d-md-none fw-semibold">
              {to12HourTime(event.startTime)}
            </span>
          </div>
          {event.service?.defaultDuration && (
            <div>
              <span className="text-muted small d-block mb-1">Duration</span>
              <span className="fw-semibold">{event.service.defaultDuration} minutes</span>
            </div>
          )}
        </div>

        {/* Payment Info */}
        {!isZeroAmount ? (
          <div className="p-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted">Subtotal</span>
              <span className="text-muted">{formatPrice(displaySubtotal)}</span>
            </div>
            {displayTax > 0 && (
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="text-muted">Tax</span>
                <span className="text-muted">{formatPrice(displayTax)}</span>
              </div>
            )}
            <hr className="my-2" style={{ borderTop: '1px solid #dee2e6' }} />
            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="fw-semibold">{getChargeLabel()}</span>
              <span className="fw-bold text-primary fs-5">{formatPrice(displayAmount)}</span>
            </div>
          </div>
        ) : (
          <div className="p-4 rounded" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-1">
              <span className="fw-semibold">{getChargeLabel()}</span>
              <span className="text-muted">Pay at appointment</span>
            </div>
          </div>
        )}
      </div>

      {!isLast && (
        <hr className="m-0" style={{ borderTop: '2px solid #dee2e6' }} />
      )}
    </>
  );
};